/**
 * Collection direction B, "fit-first filter bar" (owner pick 13/10, built 14/10; business/Shopify#52).
 * Progressive enhancement for sections/spo-collection-grid.liquid. Without this file the page still works:
 * the filter form submits as a GET, "Load more" is a ?page=N link, sort is a form field.
 *
 *   - Filters / sort: re-render the section with the Section Rendering API, pushState the URL, keep the open
 *     popover and the focused control.
 *   - Load more: append the next page's cards, replaceState the URL, focus the first new card.
 *   - Vehicle: read the C: Year Make Model app's saved vehicle (cookies ymm_values / ymm_labels, read only),
 *     match it against Odoo's fitment summary from sections/spo-fit-index (respecting the current filters and
 *     sort), draw the matching cards from /products/{handle}?view=spo-card, universal parts as their own group,
 *     or the empty state with "Ask us with your rego or VIN".
 *   - Vehicle hubs (#54, owner pick B 14/10; data-hub on the element): the vehicle is the hub's model; the year or
 *     year group comes from the hub's "Which {model} is yours?" row (sections/spo-hub-hero) or a saved vehicle of the
 *     same model. A saved vehicle of another model never filters: it gets a quiet line with a link to its parts.
 *     Store mode (data-hub-source="store", the hub collection is still empty): the parts Odoo's fitment summary
 *     lists for the model come from the store-wide fit index, with category chips built here. Card fit lines name
 *     the model and its years ("Fits: Hilux 2015–2022 +1").
 *   - Search vehicle banner (#54, search A + C): "Looking for Toyota Hilux parts?" + a year filters the results
 *     with the same fit index (data-search-vehicle on the banner, sections/spo-search-head).
 * No dependencies. Never reads spo.ktype.
 */
(() => {
  if (customElements.get('spo-collection')) return;

  const FIT_OFF_KEY = 'spo-fit-off';
  const PAGE = 24;
  const UNI_FIRST = 4;

  /** @param {string} s */
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  /** @param {string} s */
  const nice = (s) => String(s || '').split(/[-_\s]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

  /** @param {string} name */
  const cookie = (name) => {
    const hit = document.cookie.split('; ').find((c) => c.startsWith(name + '='));
    if (!hit) return '';
    try {
      return decodeURIComponent(hit.slice(name.length + 1));
    } catch (e) {
      return hit.slice(name.length + 1);
    }
  };

  /**
   * The app's saved vehicle, or null.
   * @returns {{raw: string, year: number, make: string, model: string, keys: string[], label: string, short: string} | null}
   */
  const savedVehicle = () => {
    const raw = cookie('ymm_values');
    if (!raw) return null;
    const values = raw.split('~');
    const labels = cookie('ymm_labels').split('~');
    const tag = {};
    const label = {};
    for (const [i, part] of values.entries()) {
      const at = part.indexOf('_');
      if (at < 1) continue;
      tag[part.slice(0, at)] = part.slice(at + 1);
      if (labels[i]) label[part.slice(0, at)] = labels[i].trim();
    }
    if (!tag.mk || !tag.md) return null;
    const year = parseInt(String(tag.yr || '').replace(/\D/g, ''), 10) || 0;
    const make = label.mk || nice(tag.mk);
    const model = label.md || nice(tag.md);
    const keys = [...new Set([slug(make + ' ' + model), slug(tag.mk + ' ' + tag.md)])];
    return { raw, year, make, model, keys, label: [year || '', make, model].filter(Boolean).join(' '), short: model };
  };

  /**
   * Does Odoo's fitment summary list this vehicle? "Toyota Hilux (2015 2016 2017) Mazda BT-50 (2011 2012)".
   * @param {string} summary
   * @param {{year: number, keys: string[]}} v
   */
  const fits = (summary, v) => {
    if (!summary) return false;
    for (const m of summary.matchAll(/([^()]+?)\s*\(([^)]*)\)/g)) {
      if (!v.keys.includes(slug(m[1]))) continue;
      if (!v.year) return true;
      if (m[2].split(/\s+/).includes(String(v.year))) return true;
    }
    return false;
  };

  /**
   * Hub / search vehicle match: the summary lists this model, and (when a year or year group is set) one of its
   * listed years falls in [from, to].
   * @param {string} summary
   * @param {{keys: string[], from: number, to: number}} f
   */
  const fitsRange = (summary, f) => {
    if (!summary) return false;
    for (const m of summary.matchAll(/([^()]+?)\s*\(([^)]*)\)/g)) {
      if (!f.keys.includes(slug(m[1]))) continue;
      if (!f.from) return true;
      if (m[2].split(/\s+/).some((y) => +y >= f.from && +y <= f.to)) return true;
    }
    return false;
  };

  /**
   * This model's listed years in a summary, as "2015–2022", and how many other models it lists.
   * @param {string} summary @param {string[]} keys
   */
  const modelYears = (summary, keys) => {
    let years = [];
    let others = 0;
    for (const m of String(summary || '').matchAll(/([^()]+?)\s*\(([^)]*)\)/g)) {
      if (keys.includes(slug(m[1]))) years = years.concat(m[2].split(/\s+/).map(Number).filter(Boolean));
      else others += 1;
    }
    if (!years.length) return null;
    const lo = Math.min(...years);
    const hi = Math.max(...years);
    return { span: lo === hi ? String(lo) : `${lo}–${hi}`, lo, hi, others };
  };

  /** One polite live region for the whole page (outside the re-rendered section). */
  const announce = (() => {
    let region = null;
    return (text) => {
      if (!region) {
        region = document.createElement('p');
        region.className = 'visually-hidden';
        region.setAttribute('aria-live', 'polite');
        document.body.append(region);
      }
      region.textContent = '';
      requestAnimationFrame(() => {
        region.textContent = text;
      });
    };
  })();

  /** Cards fetched for the vehicle view, cached for the visit. */
  const cardCache = new Map();
  /** @param {string} handle */
  const fetchCard = async (handle) => {
    if (cardCache.has(handle)) return cardCache.get(handle).cloneNode(true);
    const res = await fetch(`/products/${encodeURIComponent(handle)}?view=spo-card`);
    if (!res.ok) throw new Error(`card ${handle}: ${res.status}`);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const li = doc.querySelector('li.spo-coll__item');
    if (!li) throw new Error(`card ${handle}: no item`);
    cardCache.set(handle, li);
    return li.cloneNode(true);
  };
  /** Fetch cards with a small concurrency limit, keep order. @param {string[]} handles */
  const fetchCards = async (handles) => {
    const out = new Array(handles.length);
    let next = 0;
    const worker = async () => {
      while (next < handles.length) {
        const i = next++;
        try {
          out[i] = await fetchCard(handles[i]);
        } catch (e) {
          out[i] = null;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, handles.length) }, worker));
    return out.filter(Boolean);
  };

  /** Fit index per URL (filters + sort), cached for the visit. */
  const indexCache = new Map();
  /** @param {string} base @param {URLSearchParams} params */
  const fetchIndex = async (base, params) => {
    const key = base + '?' + params.toString();
    if (indexCache.has(key)) return indexCache.get(key);
    const rows = [];
    for (let page = 1; page < 20; page++) {
      const p = new URLSearchParams(params);
      p.delete('page');
      if (page > 1) p.set('page', String(page));
      p.set('section_id', 'spo-fit-index');
      const res = await fetch(`${base}?${p}`);
      if (!res.ok) throw new Error(`fit index ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const node = doc.querySelector('[data-spo-fit-index]');
      if (!node) break;
      rows.push(...JSON.parse(node.textContent));
      if (page >= (parseInt(node.dataset.pages, 10) || 1)) break;
    }
    indexCache.set(key, rows);
    return rows;
  };

  /** Keep the sticky chip bar under the sticky site header. */
  const syncStickyTop = () => {
    const header = document.querySelector('.header-section');
    let top = 0;
    if (header) {
      const style = getComputedStyle(header);
      if (style.position === 'sticky' || style.position === 'fixed') {
        // The phone header slides away with a transform (assets/spo-header.js); measure where it rests, not where
        // it is mid-slide, so the bar's own slide (gtx-custom.css) uses the full header height.
        const shift = style.transform && style.transform !== 'none' ? new DOMMatrixReadOnly(style.transform).m42 : 0;
        top = Math.max(0, Math.round(header.getBoundingClientRect().bottom - shift));
      }
    }
    document.documentElement.style.setProperty('--spo-coll-top', top + 'px');
  };
  let stickyQueued = false;
  const queueSticky = () => {
    if (stickyQueued) return;
    stickyQueued = true;
    requestAnimationFrame(() => {
      stickyQueued = false;
      syncStickyTop();
    });
  };
  window.addEventListener('scroll', queueSticky, { passive: true });
  window.addEventListener('resize', queueSticky);

  window.addEventListener('popstate', () => {
    if (document.querySelector('spo-collection')) window.location.reload();
  });

  class SpoCollection extends HTMLElement {
    /** @type {AbortController | null} */
    #controller = null;
    /** @type {string[]} */
    #fitHandles = [];
    #fitShown = 0;

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-filter-form]');
      this.vehicle = savedVehicle();
      syncStickyTop();

      this.form?.addEventListener('change', (event) => this.#onChange(event));
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#render(this.#formUrl());
      });
      this.addEventListener('click', (event) => this.#onClick(event));
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('details.spo-pop[open]');
        if (!open) return;
        open.open = false;
        open.querySelector('summary')?.focus();
      });
      this.#outside = (event) => {
        for (const d of this.querySelectorAll('details.spo-pop[open]')) {
          if (!d.contains(event.target)) d.open = false;
        }
      };
      document.addEventListener('click', this.#outside);
      for (const d of this.querySelectorAll('details.spo-pop')) {
        d.addEventListener('toggle', () => {
          if (!d.open) return;
          for (const other of this.querySelectorAll('details.spo-pop[open]')) if (other !== d) other.open = false;
        });
      }

      if (this.dataset.hub) this.#setupHub();
      else if (document.querySelector('[data-search-vehicle]') && this.dataset.search) this.#setupSearchVehicle();
      else this.#setupVehicle();
    }

    disconnectedCallback() {
      this.#controller?.abort();
      document.removeEventListener('click', this.#outside);
    }

    /** @type {(event: Event) => void} */
    #outside = () => {};

    /** Current filter + sort params from the form (empty price fields dropped). */
    #params() {
      const params = new URLSearchParams();
      if (!this.form) return params;
      const defaultSort = this.form.dataset.defaultSort || '';
      for (const [k, v] of new FormData(this.form)) {
        if (String(v).trim() === '' || k === 'spo-cat') continue;
        if (k === 'sort_by' && v === defaultSort) continue;
        params.append(k, String(v));
      }
      return params;
    }

    #formUrl() {
      const params = this.#params();
      const qs = params.toString();
      return this.dataset.url + (qs ? '?' + qs : '');
    }

    /** @param {Event} event */
    #onChange(event) {
      const target = /** @type {HTMLInputElement} */ (event.target);
      // Client-side category chips (hub store mode) filter the drawn list, not the server query.
      if (target.name === 'spo-cat') {
        this.#applyHub();
        return;
      }
      if (target.type === 'number') {
        // Price: apply when the shopper leaves the field or presses Enter (change), not per keystroke.
      }
      this.#render(this.#formUrl(), { focusName: target.name, focusValue: target.value });
    }

    /** @param {MouseEvent} event */
    #onClick(event) {
      const el = /** @type {HTMLElement} */ (event.target).closest('a, button');
      if (!el || !this.contains(el)) return;

      if (el.matches('[data-pop-close]')) {
        const d = el.closest('details');
        if (d) {
          d.open = false;
          d.querySelector('summary')?.focus();
        }
        return;
      }
      if (el.matches('[data-ajax-link]')) {
        event.preventDefault();
        this.#render(/** @type {HTMLAnchorElement} */ (el).href);
        return;
      }
      if (el.matches('[data-load-more]')) {
        event.preventDefault();
        this.#loadMore(/** @type {HTMLAnchorElement} */ (el));
        return;
      }
      if (el.matches('[data-change-vehicle]')) {
        if (this.#filter) this.#focusYear();
        else this.#openVehicleSearch();
        return;
      }
      if (this.#filter !== undefined && el.matches('[data-fit-chip], [data-fit-toggle], [data-fit-off]')) {
        // Hub / search vehicle: the chip and "Show all years" switch the year filter on and off.
        if (el.matches('[data-fit-chip]') && !this.#filter && !this.#lastFilter) {
          this.#focusYear();
          return;
        }
        const on = el.matches('[data-fit-off]') ? false : !this.#filter;
        this.#setFilter(on ? this.#lastFilter : null);
        return;
      }
      if (el.matches('[data-fit-chip]')) {
        if (!this.vehicle) {
          this.#openVehicleSearch();
          return;
        }
        this.#setFit(el.getAttribute('aria-pressed') !== 'true');
        return;
      }
      if (el.matches('[data-fit-toggle]')) {
        this.#setFit(el.getAttribute('aria-pressed') !== 'true');
        return;
      }
      if (el.matches('[data-fit-off]')) {
        this.#setFit(false);
        return;
      }
      if (el.matches('[data-fit-load-more]')) {
        this.#drawFit(true);
      }
    }

    /**
     * Re-render this section for a URL (filters, sort, clear). Keeps the open popover and the focused control.
     * @param {string} url
     * @param {{focusName?: string, focusValue?: string}} [opts]
     */
    async #render(url, opts = {}) {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const openPop = this.querySelector('details.spo-pop[open]')?.dataset.pop;
      const target = new URL(url, location.origin);
      target.searchParams.delete('page');
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(fetchUrl, { signal: this.#controller.signal });
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('spo-collection');
        if (!fresh) throw new Error('no section');
        history.pushState({ spo: true }, '', target.pathname + target.search);
        const wrapper = this.parentElement;
        this.replaceWith(fresh);
        const now = wrapper?.querySelector('spo-collection') || fresh;
        if (openPop) {
          const d = now.querySelector(`details.spo-pop[data-pop="${CSS.escape(openPop)}"]`);
          if (d) d.open = true;
        }
        if (opts.focusName) {
          const sel = `[name="${CSS.escape(opts.focusName)}"]` + (opts.focusValue && opts.focusName !== 'sort_by' ? `[value="${CSS.escape(opts.focusValue)}"]` : '');
          now.querySelector(sel)?.focus({ preventScroll: true });
        }
        announce(now.querySelector('[data-count]')?.textContent.trim() || '');
      } catch (e) {
        if (/** @type {Error} */ (e).name === 'AbortError') return;
        window.location.href = target.toString();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    /** Append the next page of cards. @param {HTMLAnchorElement} link */
    async #loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Loading…';
      const next = new URL(link.href, location.origin);
      const fetchUrl = new URL(next);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const items = [...doc.querySelectorAll('[data-grid] > li')];
        const grid = this.querySelector('[data-grid]');
        const more = this.querySelector('[data-more]');
        const freshMore = doc.querySelector('[data-more]');
        if (!grid || !items.length) throw new Error('no items');
        grid.append(...items);
        if (more && freshMore) {
          // The new block reports the page range; restate it as a running total from the first card.
          const shown = freshMore.querySelector('.spo-coll__shown');
          if (shown) shown.textContent = shown.textContent.replace(/Showing\s+\d+–/, 'Showing ');
          more.replaceWith(freshMore);
        }
        history.replaceState({ spo: true }, '', next.pathname + next.search);
        this.#hubLines();
        const total = grid.children.length;
        announce(`${items.length} more loaded, ${total} shown`);
        items[0].querySelector('a[href]')?.focus({ preventScroll: false });
      } catch (e) {
        window.location.href = next.toString();
      }
    }

    /* ---------- Vehicle ---------- */

    #setupVehicle() {
      const chip = this.querySelector('[data-fit-chip]');
      const chipLabel = this.querySelector('[data-fit-chip-label]');
      if (!this.vehicle) {
        document.documentElement.classList.remove('spo-has-vehicle');
        if (chipLabel) chipLabel.textContent = 'Fits my vehicle';
        chip?.setAttribute('aria-pressed', 'false');
        return;
      }
      if (chipLabel) chipLabel.textContent = `Fits my ${this.vehicle.short}`;
      for (const n of this.querySelectorAll('[data-vehicle-name]')) n.textContent = this.vehicle.label;
      const ask = this.querySelector('[data-fit-ask]');
      if (ask) {
        const u = new URL(ask.getAttribute('href') || '', location.origin);
        u.searchParams.set('part', `${this.dataset.collectionTitle} for my ${this.vehicle.label}`);
        ask.setAttribute('href', u.pathname + u.search);
      }
      // Search (#54): results are never narrowed by the saved vehicle until the shopper asks (the fit chip), so a
      // part-number search still shows the part.
      const off = document.documentElement.classList.contains('spo-fit-off') || !!this.dataset.search;
      this.#applyFit(!off);
    }

    /** @param {boolean} on */
    #setFit(on) {
      try {
        if (on) sessionStorage.removeItem(FIT_OFF_KEY);
        else sessionStorage.setItem(FIT_OFF_KEY, this.vehicle?.raw || '');
      } catch (e) {}
      document.documentElement.classList.toggle('spo-fit-off', !on);
      this.#applyFit(on);
      announce(on ? `Showing ${this.dataset.noun} that fit your ${this.vehicle?.label}` : `Showing all ${this.dataset.noun}`);
    }

    /** @param {boolean} on */
    async #applyFit(on) {
      const chip = this.querySelector('[data-fit-chip]');
      const toggle = this.querySelector('[data-fit-toggle]');
      chip?.setAttribute('aria-pressed', String(on));
      if (toggle) {
        toggle.setAttribute('aria-pressed', String(on));
        toggle.textContent = on ? 'Show all' : 'Show only these';
      }
      const fitBox = this.querySelector('[data-fit-results]');
      const empty = this.querySelector('[data-fit-empty]');
      const text = this.querySelector('[data-vehicle-text]');

      let rows;
      try {
        rows = await fetchIndex(this.dataset.search ? '/search' : this.dataset.url || location.pathname, this.#params());
      } catch (e) {
        // No fit data: fall back to the normal grid, say so plainly.
        if (text) text.textContent = `Couldn't check fit for your ${this.vehicle.label} just now. Each listing shows the vehicles it fits.`;
        this.classList.remove('is-fit');
        this.classList.add('is-fit-ready');
        return;
      }
      const v = this.vehicle;
      const matched = rows.filter((r) => !r[2] && fits(r[3], v));
      const universal = rows.filter((r) => r[2]);
      // Sold-out parts last, keeping the current sort within each group.
      const order = (list) => [...list.filter((r) => r[1]), ...list.filter((r) => !r[1])].map((r) => r[0]);
      this.#fitHandles = order(matched);
      this.universalHandles = order(universal);
      // 0 matches: the bar turns neutral (grey, no ✓); the empty state below carries the message (#52 item 6b).
      this.querySelector('[data-vehicle-bar]')?.classList.toggle('spo-coll__vehicle--none', matched.length === 0);

      if (text) {
        const strong = (s) => {
          const b = document.createElement('strong');
          b.textContent = s;
          return b;
        };
        text.replaceChildren(
          strong(`${matched.length} of ${rows.length} ${this.dataset.noun}`),
          matched.length === 1 ? ' is listed for your ' : ' are listed for your ',
          strong(v.label)
        );
      }

      if (!on) {
        this.classList.remove('is-fit');
        fitBox?.setAttribute('hidden', '');
        empty?.setAttribute('hidden', '');
        this.classList.add('is-fit-ready');
        return;
      }

      // Fill the vehicle view while it is still hidden, then swap it in one step (no jump while cards load).
      this.#fitShown = 0;
      this.querySelector('[data-fit-grid]')?.replaceChildren();
      this.querySelector('[data-fit-more]')?.setAttribute('hidden', '');
      if (matched.length) await this.#drawFit(false);
      await this.#drawUniversal();
      if (!matched.length) fitBox?.before(empty);
      empty?.toggleAttribute('hidden', matched.length > 0);
      fitBox?.removeAttribute('hidden');
      this.classList.add('is-fit');
      this.classList.add('is-fit-ready');
    }

    /** Draw the next PAGE matching cards. @param {boolean} more */
    async #drawFit(more) {
      const grid = this.querySelector('[data-fit-grid]');
      const box = this.querySelector('[data-fit-more]');
      const button = this.querySelector('[data-fit-load-more]');
      if (!grid) return;
      const slice = this.#fitHandles.slice(this.#fitShown, this.#fitShown + PAGE);
      if (button) button.setAttribute('aria-busy', 'true');
      const cards = await fetchCards(slice);
      grid.append(...cards);
      this.#fitShown += slice.length;
      if (button) button.removeAttribute('aria-busy');
      const total = this.#fitHandles.length;
      if (box) {
        box.toggleAttribute('hidden', total <= PAGE);
        const shown = box.querySelector('[data-fit-shown]');
        if (shown) shown.textContent = `Showing ${this.#fitShown} of ${total}`;
        button?.toggleAttribute('hidden', this.#fitShown >= total);
      }
      if (more && cards[0]) {
        cards[0].querySelector('a[href]')?.focus();
        announce(`${cards.length} more loaded, ${this.#fitShown} shown`);
      }
    }

    /**
     * Universal parts sit in their own group below the vehicle's parts (#52 item 6, owner 14/10): the first
     * UNI_FIRST cards, then "Show all N" draws the rest. With 0 vehicle matches the empty state comes first.
     */
    async #drawUniversal() {
      const box = this.querySelector('[data-fit-universal]');
      const grid = this.querySelector('[data-fit-universal-grid]');
      if (!box || !grid) return;
      const all = this.universalHandles || [];
      const n = this.querySelector('[data-fit-universal-n]');
      if (n) n.textContent = all.length ? `(${all.length})` : '';
      const more = this.querySelector('[data-fit-universal-more]');
      const button = this.querySelector('[data-fit-universal-all]');
      if (!all.length) {
        box.setAttribute('hidden', '');
        return;
      }
      grid.replaceChildren(...(await fetchCards(all.slice(0, UNI_FIRST))));
      box.removeAttribute('hidden');
      more?.toggleAttribute('hidden', all.length <= UNI_FIRST);
      if (button && all.length > UNI_FIRST) {
        button.textContent = `Show all ${all.length}`;
        button.onclick = async () => {
          button.setAttribute('aria-busy', 'true');
          const cards = await fetchCards(all.slice(UNI_FIRST));
          grid.append(...cards);
          more?.setAttribute('hidden', '');
          cards[0]?.querySelector('a[href]')?.focus();
          announce(`${all.length} universal parts shown`);
        };
      }
    }

    /* ---------- Vehicle hub and search vehicle (#54) ---------- */

    /** @type {{keys: string[], from: number, to: number, label: string} | null | undefined} undefined = not a hub/search-vehicle page */
    #filter = undefined;
    /** @type {{keys: string[], from: number, to: number, label: string} | null} */
    #lastFilter = null;
    /** @type {string[]} */
    #hubKeys = [];
    #store = false;
    /** @type {any[] | null} */
    #storeRows = null;

    #focusYear() {
      const select = document.querySelector('[data-hub-year], [data-search-vehicle] select');
      if (!select) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const header = document.querySelector('.header-section');
      const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;
      const row = select.closest('[data-hub-year-row], [data-search-vehicle]') || select;
      window.scrollTo({ top: Math.max(0, row.getBoundingClientRect().top + window.scrollY - headerH - 8), behavior: reduce ? 'auto' : 'smooth' });
      window.setTimeout(() => select.focus({ preventScroll: true }), reduce ? 0 : 400);
    }

    /** Hub page: wire the year row, the saved vehicle, store mode, card lines. */
    #setupHub() {
      const make = this.dataset.hubMake || '';
      const model = this.dataset.hubModel || '';
      // data-hub-model is the label ("Hilux", or "Mazda 3" for short names); data-hub-name is make + model.
      this.hubName = this.dataset.hubName || `${make} ${model}`.trim();
      this.hubModel = model;
      this.#hubKeys = [slug(this.hubName), this.dataset.hub || ''].filter(Boolean);
      this.#store = this.dataset.hubSource === 'store';
      this.#filter = null;
      this.vehicle = null;

      const row = document.querySelector('[data-hub-year-row]');
      const select = /** @type {HTMLSelectElement | null} */ (row?.querySelector('[data-hub-year]'));
      const key = `spo-hub-year:${this.dataset.hub}`;

      // A saved vehicle: same model -> its year; another model -> the quiet line, no filter.
      const saved = savedVehicle();
      let start = null;
      try {
        const kept = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (kept && kept.from) start = kept;
      } catch (e) {}
      if (saved && saved.keys.some((k) => this.#hubKeys.includes(k))) {
        if (!start && saved.year) start = { from: saved.year, to: saved.year };
      } else if (saved) {
        const text = document.querySelector('[data-hub-other-text]');
        const link = /** @type {HTMLAnchorElement | null} */ (document.querySelector('[data-hub-other-link]'));
        if (text) text.textContent = `Your saved vehicle is a ${saved.label}:`;
        if (link) {
          let urls = {};
          try {
            urls = JSON.parse(document.querySelector('[data-spo-hub-urls]')?.textContent || '{}');
          } catch (e) {}
          const own = saved.keys.map((k) => urls[k]).find(Boolean);
          link.href = own || window.spoVehicle?.get()?.url || '/pages/product-result';
          link.textContent = 'see its parts';
          link.setAttribute('aria-label', `See parts for your ${saved.label}`);
        }
      }

      if (row && !row.dataset.bound) {
        row.dataset.bound = '1';
        row.addEventListener('click', (event) => {
          const b = /** @type {HTMLElement} */ (event.target).closest('[data-hub-range]');
          if (!b) return;
          const now = document.querySelector('spo-collection');
          const on = b.getAttribute('aria-pressed') !== 'true';
          const [from, to] = String(b.getAttribute('data-hub-range')).split('-').map(Number);
          if (select) select.value = '';
          now?.dispatchEvent(new CustomEvent('spo:hub-filter', { detail: on ? { from, to, code: b.dataset.hubCode || '' } : null }));
        });
        row.querySelector('[data-hub-year-form]')?.addEventListener('submit', (event) => {
          event.preventDefault();
          const y = parseInt(select?.value || '', 10) || 0;
          document.querySelector('spo-collection')?.dispatchEvent(new CustomEvent('spo:hub-filter', { detail: y ? { from: y, to: y } : null, bubbles: false }));
          document.querySelector('spo-collection')?.dispatchEvent(new CustomEvent('spo:hub-scroll'));
        });
        select?.addEventListener('change', () => {
          const y = parseInt(select.value, 10) || 0;
          document.querySelector('spo-collection')?.dispatchEvent(new CustomEvent('spo:hub-filter', { detail: y ? { from: y, to: y } : null }));
        });
      }
      this.addEventListener('spo:hub-filter', (event) => {
        const d = /** @type {CustomEvent} */ (event).detail;
        this.#setFilter(d ? this.#makeFilter(d.from, d.to, d.code) : null);
      });
      this.addEventListener('spo:hub-scroll', () => {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const header = document.querySelector('.header-section');
        const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;
        window.scrollTo({ top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - headerH - 8), behavior: reduce ? 'auto' : 'smooth' });
      });
      const ask = this.querySelector('[data-fit-ask]');
      if (ask) this.askBase = ask.getAttribute('href') || '';

      // Card lines name the model and its years.
      new MutationObserver(() => this.#hubLines()).observe(this, { childList: true, subtree: true });
      this.#hubLines();

      if (start) {
        if (select && start.from === start.to) select.value = String(start.from);
        this.#setFilter(this.#makeFilter(start.from, start.to, start.code || ''), true);
      } else {
        this.#setFilter(null, true);
      }
    }

    /** @param {number} from @param {number} to @param {string} [code] */
    #makeFilter(from, to, code = '') {
      const years = from === to ? String(from) : `${from}–${to}`;
      const name = this.hubName || this.searchName || '';
      return { keys: this.#hubKeys, from, to, code, label: `${code ? code + ' ' : ''}${years} ${name}`.trim() };
    }

    /**
     * Set or clear the year filter, mark the year row, remember it for the visit, redraw.
     * @param {{keys: string[], from: number, to: number, label: string, code?: string} | null} f
     * @param {boolean} [quiet]
     */
    #setFilter(f, quiet = false) {
      this.#filter = f;
      if (f) this.#lastFilter = f;
      for (const b of document.querySelectorAll('[data-hub-range]')) {
        b.setAttribute('aria-pressed', String(!!f && b.getAttribute('data-hub-range') === `${f.from}-${f.to}`));
      }
      const select = /** @type {HTMLSelectElement | null} */ (document.querySelector('[data-hub-year], [data-search-vehicle] select'));
      if (select && f && f.from === f.to) select.value = String(f.from);
      if (select && !f) select.value = '';
      try {
        const key = this.dataset.hub ? `spo-hub-year:${this.dataset.hub}` : '';
        if (key) {
          if (f) sessionStorage.setItem(key, JSON.stringify({ from: f.from, to: f.to, code: f.code || '' }));
          else sessionStorage.removeItem(key);
        }
      } catch (e) {}
      const chipLabel = this.querySelector('[data-fit-chip-label]');
      if (chipLabel) chipLabel.textContent = f ? `Fits a ${f.label.replace(this.hubName || this.searchName || '', '').trim()} ${this.hubModel || this.searchModel || ''}`.replace(/\s+/g, ' ').trim() : `Fits my ${this.hubModel || this.searchModel || 'vehicle'}`;
      this.querySelector('[data-fit-chip]')?.setAttribute('aria-pressed', String(!!f));
      this.#applyHub().then(() => {
        if (!quiet) {
          const n = this.querySelector('[data-vehicle-text]')?.textContent || this.querySelector('[data-count]')?.textContent || '';
          announce(n.trim());
        }
      });
    }

    /** Index rows for this page: the store-wide index in store mode, else this collection's / search's own. */
    async #rows() {
      if (this.#store) {
        if (!this.#storeRows) {
          const all = await fetchIndex('/collections/all', new URLSearchParams());
          this.#storeRows = all.filter((r) => !r[2] && fitsRange(r[3], { keys: this.#hubKeys, from: 0, to: 0 }));
        }
        return this.#storeRows;
      }
      const base = this.dataset.search ? '/search' : this.dataset.url || location.pathname;
      return fetchIndex(base, this.#params());
    }

    async #applyHub() {
      if (this.#filter === undefined) return;
      const f = this.#filter;
      const fitBox = this.querySelector('[data-fit-results]');
      const empty = this.querySelector('[data-fit-empty]');
      const bar = this.querySelector('[data-vehicle-bar]');
      const text = this.querySelector('[data-vehicle-text]');
      const count = this.querySelector('[data-count]');
      this.classList.toggle('is-hub-year', !!f);
      if (bar) bar.style.display = f ? 'flex' : 'none';

      let rows;
      try {
        rows = await this.#rows();
      } catch (e) {
        if (text) text.textContent = `Couldn't check the years just now. Each listing shows the years it is listed for.`;
        this.classList.add('is-fit-ready');
        return;
      }
      if (this.#store) this.#storeChips(rows, f);
      const cats = [...this.querySelectorAll('input[name="spo-cat"]:checked')].map((i) => /** @type {HTMLInputElement} */ (i).value);
      const inCats = (r) => !cats.length || cats.includes(r[4] || '');
      const pool = this.dataset.search && f ? rows : rows;
      const matched = (f ? pool.filter((r) => fitsRange(r[3], f)) : pool).filter(inCats);
      const order = (list) => [...list.filter((r) => r[1]), ...list.filter((r) => !r[1])].map((r) => r[0]);

      this.#fillHubTable(rows);
      if (count && this.#store) count.textContent = f ? `${matched.length} of ${rows.length} parts` : `${matched.length} parts`;
      bar?.classList.toggle('spo-coll__vehicle--none', !!f && matched.length === 0);
      if (text && f) {
        const strong = (t) => {
          const b = document.createElement('strong');
          b.textContent = t;
          return b;
        };
        const total = this.#store || !this.dataset.search ? rows.filter(inCats).length : rows.length;
        const noun = this.dataset.search ? 'results' : 'parts';
        text.replaceChildren(strong(`${matched.length} of ${total} ${noun}`), matched.length === 1 ? ' is listed for a ' : ' are listed for a ', strong(f.label));
      }
      const changeBtn = this.querySelector('[data-vehicle-bar] [data-change-vehicle]');
      if (changeBtn) changeBtn.textContent = 'Change year';
      const toggle = this.querySelector('[data-fit-toggle]');
      if (toggle) {
        toggle.textContent = this.dataset.search ? 'Show all results' : 'Show all years';
        toggle.setAttribute('aria-pressed', 'true');
      }
      for (const nm of this.querySelectorAll('[data-vehicle-name]')) nm.textContent = f ? f.label : this.hubName || '';
      const offBtn = this.querySelector('[data-fit-off]');
      if (offBtn) offBtn.textContent = this.dataset.search ? 'Show all results' : 'Show all years';
      const ask = this.querySelector('[data-fit-ask]');
      if (ask && f) {
        const u = new URL(this.askBase || ask.getAttribute('href') || '', location.origin);
        u.searchParams.set('part', `Parts for my ${f.label}`);
        ask.setAttribute('href', u.pathname + u.search);
      }

      const hubEmpty = this.querySelector('[data-hub-empty]');
      if (!f && !this.#store) {
        // The server-rendered grid is the model's list already.
        this.classList.remove('is-fit');
        fitBox?.setAttribute('hidden', '');
        empty?.setAttribute('hidden', '');
        this.classList.add('is-fit-ready');
        this.#hubLines();
        return;
      }
      this.#fitHandles = order(matched);
      this.universalHandles = [];
      this.#fitShown = 0;
      this.querySelector('[data-fit-grid]')?.replaceChildren();
      this.querySelector('[data-fit-more]')?.setAttribute('hidden', '');
      this.querySelector('[data-fit-universal]')?.setAttribute('hidden', '');
      if (matched.length) await this.#drawFit(false);
      if (this.#store && !f && !matched.length) {
        // No Odoo-listed parts either: the server-rendered hub empty state stays.
        fitBox?.setAttribute('hidden', '');
        this.classList.remove('is-fit');
        return;
      }
      hubEmpty?.setAttribute('hidden', '');
      if (!matched.length) fitBox?.before(empty);
      empty?.toggleAttribute('hidden', matched.length > 0);
      fitBox?.removeAttribute('hidden');
      this.classList.add('is-fit', 'is-fit-ready');
      this.#hubLines();
    }

    /** Store mode: category chips with counts from the model's parts (Product type), as labelled checkboxes. */
    #storeChips(rows, f) {
      const chips = this.querySelector('.spo-coll__chips');
      if (!chips) return;
      let group = chips.querySelector('[data-store-cats]');
      const chosen = new Set([...(group?.querySelectorAll('input:checked') || [])].map((i) => /** @type {HTMLInputElement} */ (i).value));
      const pool = f ? rows.filter((r) => fitsRange(r[3], f)) : rows;
      const counts = new Map();
      for (const r of pool) if (r[4]) counts.set(r[4], (counts.get(r[4]) || 0) + 1);
      const list = [...counts.entries()].sort((a, b) => b[1] - a[1]);
      if (!group) {
        group = document.createElement('div');
        group.className = 'spo-coll__cats';
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', 'Category');
        group.setAttribute('data-store-cats', '');
        group.style.order = '0';
        chips.querySelector('[data-fit-chip]')?.after(group);
        for (const sort of chips.querySelectorAll('.spo-fchip--sort')) /** @type {HTMLElement} */ (sort).hidden = true;
      }
      if (list.length < 2) {
        group.replaceChildren();
        return;
      }
      group.replaceChildren(
        ...list.map(([type, n]) => {
          const label = document.createElement('label');
          label.className = 'spo-fchip spo-fchip--check spo-fchip--cat' + (chosen.has(type) ? ' is-active' : '');
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.className = 'spo-fchip__input';
          input.name = 'spo-cat';
          input.value = type;
          input.checked = chosen.has(type);
          const name = document.createElement('span');
          name.textContent = type.toLowerCase().replace(/^./, (c) => c.toUpperCase()).replace(/^Ev /, 'EV ');
          const num = document.createElement('span');
          num.className = 'spo-fchip__n';
          num.textContent = String(n);
          label.append(input, name, num);
          return label;
        })
      );
    }

    /** The "parts by year" table in the hub details band (sections/spo-hub-details). */
    #fillHubTable(rows) {
      const box = document.querySelector('[data-hub-table]');
      if (!box || box.dataset.filled) return;
      const groups = [...box.querySelectorAll('thead th[data-range]')].map((th) => th.getAttribute('data-range').split('-').map(Number));
      if (!groups.length) return;
      const byType = new Map();
      for (const r of rows) {
        const t = r[4] || 'Other';
        if (!byType.has(t)) byType.set(t, groups.map(() => 0));
        groups.forEach(([from, to], i) => {
          if (fitsRange(r[3], { keys: this.#hubKeys, from, to })) byType.get(t)[i] += 1;
        });
      }
      const body = box.querySelector('tbody');
      if (!body) return;
      body.replaceChildren(
        ...[...byType.entries()]
          .sort((a, b) => b[1].reduce((x, y) => x + y, 0) - a[1].reduce((x, y) => x + y, 0))
          .map(([t, n]) => {
            const tr = document.createElement('tr');
            const th = document.createElement('th');
            th.scope = 'row';
            th.textContent = t.toLowerCase().replace(/^./, (c) => c.toUpperCase()).replace(/^Ev /, 'EV ');
            tr.append(th, ...n.map((v) => {
              const td = document.createElement('td');
              td.textContent = v ? String(v) : '–';
              return td;
            }));
            return tr;
          })
      );
      box.dataset.filled = '1';
      box.removeAttribute('hidden');
      document.querySelector('[data-hub-table-empty]')?.setAttribute('hidden', '');
    }

    /** Hub card lines: "Fits: Hilux 2015–2022 +1"; with a year set and a match, "✓ Fits a 2018 Hilux". */
    #hubLines() {
      if (!this.dataset.hub) return;
      const f = this.#filter;
      for (const line of this.querySelectorAll('.spo-fits-line[data-spo-fit-mmy]')) {
        const key = f ? `${f.from}-${f.to}` : 'all';
        if (line.getAttribute('data-spo-hub-line') === key) continue;
        line.setAttribute('data-spo-hub-line', key);
        line.setAttribute('data-spo-fit-done', '');
        const y = modelYears(line.getAttribute('data-spo-fit-mmy'), this.#hubKeys);
        if (!y) continue;
        line.classList.remove('spo-fits-line--universal');
        if (f && fitsRange(line.getAttribute('data-spo-fit-mmy'), f)) {
          const tick = document.createElement('span');
          tick.className = 'spo-fits-line__tick';
          tick.setAttribute('aria-hidden', 'true');
          tick.textContent = '✓';
          line.replaceChildren(tick, `Fits a ${f.from === f.to ? f.from : f.from + '–' + f.to} ${this.hubModel}`);
          line.classList.add('spo-fits-line--you');
        } else {
          line.classList.remove('spo-fits-line--you');
          line.textContent = `Fits: ${this.hubModel} ${y.span}${y.others ? ` +${y.others}` : ''}`;
        }
      }
    }

    /** Search page: "Looking for Toyota Hilux parts?" banner with a year (sections/spo-search-head). */
    #setupSearchVehicle() {
      const box = document.querySelector('[data-search-vehicle]');
      if (!box) return;
      this.#filter = null;
      this.vehicle = null;
      this.searchName = box.getAttribute('data-name') || '';
      this.searchModel = box.getAttribute('data-model') || '';
      this.#hubKeys = [box.getAttribute('data-keys') || slug(this.searchName)];
      const form = box.querySelector('form');
      const select = /** @type {HTMLSelectElement | null} */ (box.querySelector('select'));
      if (form && !form.dataset.bound) {
        form.dataset.bound = '1';
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          const y = parseInt(select?.value || '', 10) || 0;
          document.querySelector('spo-collection')?.dispatchEvent(new CustomEvent('spo:hub-filter', { detail: y ? { from: y, to: y } : null }));
        });
      }
      this.addEventListener('spo:hub-filter', (event) => {
        const d = /** @type {CustomEvent} */ (event).detail;
        this.#setFilter(d ? this.#makeFilter(d.from, d.to) : null);
      });
      const pre = parseInt(select?.value || '', 10) || 0;
      this.#setFilter(pre ? this.#makeFilter(pre, pre) : null, true);
    }

    #openVehicleSearch() {
      // Build 4 (14/10): the shared helper (snippets/spo-vehicle-js.liquid) does the same steps; the copy below stays
      // only as a fallback if window.spoVehicle is missing.
      if (window.spoVehicle && typeof window.spoVehicle.openSearch === 'function') {
        window.spoVehicle.openSearch();
        return;
      }
      document.documentElement.classList.add('spo-ymm-open');
      const toggle = document.querySelector('[data-spo-ymm-toggle]');
      if (toggle && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
      const row = document.querySelector('.spo-home-ymm--bar') || document.getElementById('vehicle-search');
      if (!row) return;
      // Scroll the row in under the sticky header (scrollIntoView alone would leave it behind the header).
      const header = document.querySelector('.header-section');
      const headerH = header ? Math.round(header.getBoundingClientRect().height) : 0;
      const top = row.getBoundingClientRect().top + window.scrollY - headerH - 8;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
      const field = [...row.querySelectorAll('.ss-main:not(.ss-disabled), select, input:not([type="hidden"])')].find(
        (el) => el.offsetParent !== null && !el.disabled
      );
      if (field) window.setTimeout(() => field.focus({ preventScroll: true }), reduce ? 0 : 400);
    }
  }

  customElements.define('spo-collection', SpoCollection);
})();
